<template>
  <transition name="reminder-banner">
    <div
      v-if="visible && reminder"
      class="reminder-banner"
      :class="{ 'reminder-banner--over-tabs': overTabBar }"
      role="status"
      aria-live="polite"
    >
      <span class="reminder-banner__icon" aria-hidden="true">
        <ion-icon :icon="calendarOutline" />
      </span>
      <div class="reminder-banner__copy">
        <p class="reminder-banner__title">{{ reminder.title || 'Upcoming event' }}</p>
        <p class="reminder-banner__body">{{ body }}</p>
      </div>
      <div class="reminder-banner__actions">
        <button
          type="button"
          class="reminder-banner__btn reminder-banner__btn--ghost"
          @click="onDismiss"
        >
          Dismiss
        </button>
        <button
          type="button"
          class="reminder-banner__btn reminder-banner__btn--primary"
          @click="onOpen"
        >
          Open
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * The in-app event reminder banner.
 *
 * A persistent banner rather than a modal or a toast, for the same reasons as
 * the PWA update prompt it is cloned from: a modal would block a half-finished
 * leave request for something that is never an emergency, and a `duration: 0`
 * toast collides with the app's transient toasts and cannot be anchored above
 * the custom tab bar on standalone routes.
 *
 * Mounted in App.vue, so a reminder reaches the standalone authed pages
 * (/leaves, /payslips, /events/…) as well as the tabs.
 *
 * The native tap listener is registered HERE, not in the engine, and that is
 * deliberate: routing is a UI concern, and `event-reminders.ts` must stay free
 * of the router so it can be started from the session store without dragging
 * navigation into the auth path.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { IonIcon } from '@ionic/vue';
import { calendarOutline } from 'ionicons/icons';
import { useRoute, useRouter } from 'vue-router';
import { dueReminders, nextReminder } from '@/services/event-reminders';
import { reminderBody } from '@/services/event-reminders-plan';
import { useSettingsStore } from '@/stores/settings.store';
import { isComposerRoute, isTabBarVisible } from '@/utils/tab-bar';
import * as localNotifications from '@/services/device/local-notifications.service';

const route = useRoute();
const router = useRouter();
const settings = useSettingsStore();

/** Re-renders the countdown copy whenever the queue changes. */
const now = ref(Date.now());

const reminder = computed(() => nextReminder.value);
const overTabBar = computed(() => isTabBarVisible(route.path));

/**
 * DEFERRED on the full-page chats, never dropped.
 *
 * Those routes own the bottom edge with a fixed composer, so the banner would
 * cover a half-typed message. The reminder stays at the head of the queue and
 * appears the moment the route changes — losing it would defeat the point of
 * having reminded at all.
 */
const visible = computed(() => reminder.value !== null && !isComposerRoute(route.path));

const body = computed(() =>
  reminder.value ? reminderBody(reminder.value, settings.eventReminderLead, now.value) : '',
);

/**
 * Keep the countdown honest for a banner the user leaves on screen.
 *
 * A phone that goes back in a pocket can leave a "20 minutes before" banner up
 * long past the event's start, where frozen copy would be actively wrong. The
 * clock runs ONLY while a banner is visible — bounded by definition, and no
 * timer at all in the overwhelmingly common case of no reminder pending.
 */
const COUNTDOWN_TICK_MS = 30 * 1000;
let countdown: ReturnType<typeof setInterval> | null = null;

function stopCountdown(): void {
  if (countdown !== null) {
    clearInterval(countdown);
    countdown = null;
  }
}

watch(
  visible,
  (shown) => {
    now.value = Date.now();
    stopCountdown();
    if (shown) {
      countdown = setInterval(() => {
        now.value = Date.now();
      }, COUNTDOWN_TICK_MS);
    }
  },
  { immediate: true },
);

// A new head entry restarts the copy from its own remaining time.
watch(reminder, () => {
  now.value = Date.now();
});

/** Drop the head of the queue; the next one (if any) slides in behind it. */
function shift(): void {
  dueReminders.value = dueReminders.value.slice(1);
}

function onDismiss(): void {
  shift();
}

function openEvent(eventId: string, occurrenceDate: string): void {
  void router.push({
    name: 'event-detail',
    params: { id: eventId },
    // A recurring series shares one id across occurrences, so the date is what
    // tells the detail page WHICH one was being reminded about.
    ...(occurrenceDate ? { query: { d: occurrenceDate } } : {}),
  });
}

function onOpen(): void {
  const current = reminder.value;
  shift();
  if (current) openEvent(current.eventId, current.occurrenceDate);
}

/** Detach for the native tap listener, resolved after mount. */
let detachTap: (() => void) | null = null;

onMounted(async () => {
  detachTap = await localNotifications.onTap(({ eventId, occurrenceDate }) => {
    openEvent(eventId, occurrenceDate);
  });
});

onBeforeUnmount(() => {
  stopCountdown();
  detachTap?.();
  detachTap = null;
});
</script>

<style scoped lang="scss">
.reminder-banner {
  position: fixed;
  left: var(--sp-4);
  right: var(--sp-4);
  bottom: calc(var(--syntry-safe-bottom, env(safe-area-inset-bottom, 0px)) + var(--sp-4));
  max-width: 480px;
  margin-inline: auto;
  /* Above the tab bar (100), below the offline banner (300) and far below
     Ionic's overlays, so a modal always covers this. Same band as the PWA
     update banner — the two never show at once in practice. */
  z-index: 200;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--syntry-radius-card);
  /* Frosted surface, matching ConfirmModal and the update banner. */
  background: rgba(var(--syntry-surface-rgb), 0.92);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid var(--syntry-border);
  box-shadow: var(--syntry-shadow-elevated);

  /* Clear the bottom tab bar when it's on screen. */
  &--over-tabs {
    bottom: calc(
      64px + var(--syntry-safe-bottom, env(safe-area-inset-bottom, 0px)) + var(--sp-3)
    );
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--syntry-brand-soft-color);
    color: var(--syntry-primary);
    font-size: 18px;
  }

  &__copy {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: var(--syntry-fs-tiny);
    font-weight: 700;
    color: var(--syntry-text-strong);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__body {
    margin: 2px 0 0;
    font-size: var(--syntry-fs-tiny);
    font-weight: 500;
    color: var(--syntry-text-muted);
    line-height: 1.3;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    flex-shrink: 0;
  }

  &__btn {
    min-height: 34px;
    padding: 0 var(--sp-3);
    border-radius: var(--syntry-radius-button);
    border: none;
    font-family: var(--syntry-font);
    font-size: var(--syntry-fs-tiny);
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &--ghost {
      background: transparent;
      color: var(--syntry-text-muted);
    }

    &--primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: var(--syntry-primary);
      color: #ffffff;
      box-shadow: var(--syntry-shadow-button);
    }
  }
}

.reminder-banner-enter-active,
.reminder-banner-leave-active {
  transition: transform 220ms ease, opacity 220ms ease;
}

.reminder-banner-enter-from,
.reminder-banner-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .reminder-banner-enter-active,
  .reminder-banner-leave-active {
    transition: none;
  }
}
</style>
